import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, CircularProgress, Button, Grid,
  Table, TableBody, TableRow, TableCell, List, ListItem, ListItemText,
  alpha, Divider, Collapse,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { getTabledDocument } from '../services/api';

// Tabled Documents detail page (#260). Mirrors NoticePaperDetail shape: hero
// header (title + chamber + parliament chips) → metadata grid → optional body
// → attached files table → View on APH button. Themed in the same orange as
// the Tabled Documents search page.
const THEME_COLOR = '#ff9800';

export default function TabledDocumentDetail() {
  const { id } = useParams();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bodyExpanded, setBodyExpanded] = useState(false);

  useEffect(() => {
    const fetchDocument = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getTabledDocument(id);
        setDocument(data);
      } catch (err) {
        console.error('Error fetching tabled document:', err);
        setError('Failed to load tabled document');
      }
      setLoading(false);
    };
    fetchDocument();
  }, [id]);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString('en-AU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const MetadataRow = ({ label, value }) => {
    if (value === null || value === undefined || value === '' ||
        (Array.isArray(value) && value.length === 0)) {
      return null;
    }
    let rendered = value;
    if (Array.isArray(value)) rendered = value.join(', ');
    else if (typeof value === 'boolean') rendered = value ? 'Yes' : 'No';
    else rendered = String(value);
    return (
      <TableRow>
        <TableCell sx={{ fontWeight: 500, color: 'text.secondary', width: '32%', py: 1 }}>
          {label}
        </TableCell>
        <TableCell sx={{ py: 1 }}>{rendered}</TableCell>
      </TableRow>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: THEME_COLOR }} />
      </Box>
    );
  }

  if (error || !document) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">{error || 'Tabled document not found'}</Typography>
        <Button
          component={RouterLink}
          to="/tableddocuments"
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Tabled Documents
        </Button>
      </Box>
    );
  }

  const content = document.content && String(document.content).trim();
  const showBody = !!content;
  const BODY_PREVIEW_LIMIT = 600;
  const longBody = content && content.length > BODY_PREVIEW_LIMIT;
  const bodyPreview = longBody ? `${content.slice(0, BODY_PREVIEW_LIMIT)}…` : content;

  return (
    <Box>
      {/* Navigation Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button
          component={RouterLink}
          to="/tableddocuments"
          startIcon={<ArrowBackIcon />}
          sx={{ color: 'text.secondary' }}
        >
          Back to Tabled Documents
        </Button>
      </Box>

      {/* Hero Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(THEME_COLOR, 0.08)} 0%, ${alpha(THEME_COLOR, 0.02)} 100%)`,
          border: '1px solid',
          borderColor: alpha(THEME_COLOR, 0.15),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <DescriptionIcon sx={{ fontSize: 48, color: THEME_COLOR }} />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: THEME_COLOR, mb: 0.5 }}
              dangerouslySetInnerHTML={{
                __html: document.title || 'Untitled Document',
              }}
            />

            {document.date && (
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                {formatDate(document.date)}
              </Typography>
            )}

            {/* Metadata chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {document.originating_chamber && (
                <Chip
                  label={document.originating_chamber}
                  size="small"
                  sx={{ bgcolor: THEME_COLOR, color: 'white' }}
                />
              )}
              {document.parliament_number && (
                <Chip
                  label={`${document.parliament_number}th Parliament`}
                  size="small"
                  variant="outlined"
                  clickable
                  component={RouterLink}
                  to={`/parliaments/${document.parliament_number}`}
                />
              )}
              {document.paper_type && (
                <Chip
                  label={document.paper_type}
                  size="small"
                  variant="outlined"
                />
              )}
              {document.is_disallowable && (
                <Chip
                  label="Disallowable"
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {document.source_url && (
                <Button
                  size="small"
                  variant="outlined"
                  href={document.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<OpenInNewIcon />}
                  sx={{ borderColor: THEME_COLOR, color: THEME_COLOR }}
                >
                  View on APH
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={showBody && document.files?.length ? 7 : 12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: THEME_COLOR }}>
              Document Information
            </Typography>
            <Table size="small">
              <TableBody>
                <MetadataRow label="Title" value={document.title} />
                <MetadataRow label="Tabled in House" value={formatDate(document.tabled_house)} />
                <MetadataRow label="Tabled in Senate" value={formatDate(document.tabled_senate)} />
                <MetadataRow label="Parliament" value={document.parliament_number} />
                <MetadataRow
                  label="Parliamentary Paper No."
                  value={document.parliamentary_paper_number}
                />
                <MetadataRow
                  label="Parliamentary Paper Year"
                  value={document.parliamentary_paper_year}
                />
                <MetadataRow label="Paper Type" value={document.paper_type} />
                <MetadataRow label="Document Type" value={document.document_type} />
                <MetadataRow label="Category" value={document.category} />
                <MetadataRow label="Author" value={document.author} />
                <MetadataRow label="Department" value={document.department} />
                <MetadataRow
                  label="Disallowable"
                  value={document.is_disallowable}
                />
                <MetadataRow label="FRL ID" value={document.frl_id || document.frli_id} />
                <MetadataRow label="Document Number" value={document.document_number} />
                <MetadataRow label="Themes" value={document.themes} />
                <MetadataRow label="Keywords" value={document.keywords} />
              </TableBody>
            </Table>

            {showBody && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: THEME_COLOR }}>
                  Document Body
                </Typography>
                <Collapse in={bodyExpanded || !longBody} collapsedSize={longBody ? 140 : 'auto'}>
                  <Typography
                    component="pre"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'inherit',
                      fontSize: '0.95rem',
                      lineHeight: 1.7,
                      m: 0,
                    }}
                  >
                    {bodyExpanded || !longBody ? content : bodyPreview}
                  </Typography>
                </Collapse>
                {longBody && (
                  <Button
                    size="small"
                    onClick={() => setBodyExpanded((v) => !v)}
                    sx={{ mt: 1, color: THEME_COLOR }}
                  >
                    {bodyExpanded ? 'Show less' : 'Show full text'}
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {document.files && document.files.length > 0 && (
          <Grid item xs={12} md={showBody ? 5 : 12}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: THEME_COLOR }}>
                Attached Files ({document.files.length})
              </Typography>
              <List dense>
                {document.files.map((file, idx) => (
                  <ListItem
                    key={file.fileId || idx}
                    sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 1.2 }}
                  >
                    <AttachFileIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <ListItemText
                      primary={file.name || 'Untitled file'}
                      secondary={
                        <>
                          {formatFileSize(file.size)}
                          {file.fileId ? ` · ID ${file.fileId}` : ''}
                        </>
                      }
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
